import React, { useState, useEffect } from 'react';
import { TrendingUp, Users, ShoppingBag, Zap, UserPlus, CreditCard, Eye, ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { adminStoresAPI, adminStatsAPI, adminPromoterMetricsAPI } from '../../utils/api';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../ui/toaster';
import { Button } from '../ui/button';

const Statistics = () => {
  const [stats, setStats] = useState(null);
  const [topStores, setTopStores] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [storesCount, setStoresCount] = useState({ active: 0, total: 0 });
  const [dashboardPromoters, setDashboardPromoters] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Estado para la fecha seleccionada (hoy por defecto)
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0]; // Formato YYYY-MM-DD
  });

  useEffect(() => {
    loadAllStats();
  }, [selectedDate]); // Recargar cuando cambie la fecha

  const loadAllStats = async () => {
    try {
      // Load overview stats
      const overviewResponse = await adminStatsAPI.getOverview();
      setStats(overviewResponse.data);

      // Load top stores
      const topStoresResponse = await adminStatsAPI.getTopStores();
      setTopStores(topStoresResponse.data.stores || []);

      // Load recent activity
      const activityResponse = await adminStatsAPI.getRecentActivity();
      setRecentActivity(activityResponse.data.activities || []);

      // Load stores count
      const storesResponse = await adminStoresAPI.getAll();
      const stores = storesResponse.data;
      setStoresCount({
        active: stores.filter(s => s.isParticipating).length,
        total: stores.length
      });

      // Load dashboard promoters con filtro por día
      const dashboardPromotersResponse = await adminPromoterMetricsAPI.getDashboardPromoters({
        date_from: selectedDate,
        date_to: selectedDate,
      });
      if (dashboardPromotersResponse?.success && dashboardPromotersResponse?.data?.promoters) {
        setDashboardPromoters(dashboardPromotersResponse.data.promoters);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar las estadísticas",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <p className="text-white">Cargando estadísticas...</p>
      </div>
    );
  }

  return (
    <>
      <div className="p-8">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Header */}
          <div>
            <h2 className="text-3xl font-bold text-white mb-2">Estadísticas</h2>
            <p className="text-[#A2A2A2]">Monitorea el rendimiento de la plataforma</p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-black border border-white/10 rounded-3xl p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <p className="text-[#A2A2A2] text-sm">Miembros totales</p>
              </div>
              <p className="text-3xl font-bold text-white">{stats?.totalMembers || 0}</p>
              <p className="text-sm text-[#A2A2A2] mt-1">{stats?.activeMembers || 0} activos</p>
            </div>

            <div className="bg-black border border-white/10 rounded-3xl p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                  <ShoppingBag className="w-5 h-5 text-white" />
                </div>
                <p className="text-[#A2A2A2] text-sm">Redenciones</p>
              </div>
              <p className="text-3xl font-bold text-white">{stats?.totalRedemptions || 0}</p>
              <p className="text-sm text-[#A2A2A2] mt-1">{stats?.thisMonth || 0} este mes</p>
            </div>

            <div className="bg-black border border-white/10 rounded-3xl p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-white" />
                </div>
                <p className="text-[#A2A2A2] text-sm">Promedio por miembro</p>
              </div>
              <p className="text-3xl font-bold text-white">{stats?.averagePerMember || 0}</p>
              <p className="text-sm text-[#A2A2A2] mt-1">beneficios/mes</p>
            </div>

            <div className="bg-black border border-white/10 rounded-3xl p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-white" />
                </div>
                <p className="text-[#A2A2A2] text-sm">Tiendas activas</p>
              </div>
              <p className="text-3xl font-bold text-white">{storesCount.active}</p>
              <p className="text-sm text-[#A2A2A2] mt-1">de {storesCount.total} total</p>
            </div>
          </div>

          {/* Top Stores */}
          {topStores.length > 0 && (
            <div className="bg-black border border-white/10 rounded-3xl p-6">
              <h3 className="text-xl font-semibold text-white mb-4">Tiendas más usadas</h3>
              <div className="space-y-3">
                {topStores.map((store, index) => (
                  <div key={store.id} className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-sm">{index + 1}</span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-white font-medium">{store.name}</span>
                        <span className="text-[#A2A2A2] text-sm">{store.redemptions} redenciones</span>
                      </div>
                      <div className="w-full h-2 bg-white/5 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-white/90 transition-all"
                          style={{ width: `${store.percentage * 5}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent Activity */}
          {recentActivity.length > 0 && (
            <div className="bg-black border border-white/10 rounded-3xl p-6">
              <h3 className="text-xl font-semibold text-white mb-4">Actividad reciente</h3>
              <div className="space-y-3">
                {recentActivity.map((activity) => (
                  <div key={activity.id} className="flex items-start justify-between gap-4 pb-3 border-b border-white/5 last:border-0">
                    <div>
                      <p className="text-white font-medium">{activity.member}</p>
                      <p className="text-[#A2A2A2] text-sm">
                        Usó <span className="text-white">{activity.benefit}</span> en {activity.store}
                      </p>
                    </div>
                    <span className="text-[#A2A2A2] text-sm flex-shrink-0">{activity.time}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Dashboard Promoters Section */}
          <div className="bg-black border border-white/10 rounded-3xl p-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
              <h3 className="text-xl font-semibold text-white">Promotores en Dashboard</h3>
              
              {/* Navegación de fechas */}
              <div className="flex items-center gap-3 flex-wrap">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const date = new Date(selectedDate);
                    date.setDate(date.getDate() - 1);
                    setSelectedDate(date.toISOString().split('T')[0]);
                  }}
                  className="border-white/10 text-white hover:bg-white/10 rounded-full"
                  title="Día anterior"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                
                <div className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full">
                  <Calendar className="w-4 h-4 text-white" />
                  <span className="text-white font-medium text-sm whitespace-nowrap">
                    {new Date(selectedDate).toLocaleDateString('es-ES', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </span>
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const date = new Date(selectedDate);
                    date.setDate(date.getDate() + 1);
                    setSelectedDate(date.toISOString().split('T')[0]);
                  }}
                  disabled={(() => {
                    const date = new Date(selectedDate);
                    date.setDate(date.getDate() + 1);
                    const nextDateStr = date.toISOString().split('T')[0];
                    const todayStr = new Date().toISOString().split('T')[0];
                    return nextDateStr > todayStr;
                  })()}
                  className="border-white/10 text-white hover:bg-white/10 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Día siguiente"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
                
                {selectedDate !== new Date().toISOString().split('T')[0] && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const today = new Date();
                      setSelectedDate(today.toISOString().split('T')[0]);
                    }}
                    className="border-white/10 text-white hover:bg-white/10 rounded-full text-xs px-3"
                    title="Ir a hoy"
                  >
                    Hoy
                  </Button>
                )}
              </div>
            </div>
            
            {dashboardPromoters.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {dashboardPromoters.map((promoter) => (
                  <div key={promoter.id} className="bg-white/5 border border-white/10 rounded-2xl p-5 hover:border-white/20 transition-colors">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                        <UserPlus className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-white font-semibold text-lg truncate">{promoter.name}</h4>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Eye className="w-4 h-4 text-[#A2A2A2]" />
                          <span className="text-[#A2A2A2] text-sm">Visitas</span>
                        </div>
                        <span className="text-white font-medium">{promoter.visits || 0}</span>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <UserPlus className="w-4 h-4 text-[#A2A2A2]" />
                          <span className="text-[#A2A2A2] text-sm">Registrados</span>
                        </div>
                        <span className="text-white font-medium">{promoter.registrations || 0}</span>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <CreditCard className="w-4 h-4 text-[#A2A2A2]" />
                          <span className="text-[#A2A2A2] text-sm">Pagos</span>
                        </div>
                        <div className="text-right">
                          <span className="text-white font-medium">{promoter.payments || 0}</span>
                          {promoter.revenue > 0 && (
                            <p className="text-[#A2A2A2] text-xs">${promoter.revenue.toFixed(2)} MXN</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-[#A2A2A2]">
                <p>No hay datos de promotores para esta fecha</p>
              </div>
            )}
          </div>
        </div>
      </div>
      <Toaster />
    </>
  );
};

export default Statistics;
